import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Check, Sparkles, Users, TreeDeciduous, PartyPopper, ShieldCheck,
  ArrowRight, Loader2, Crown, Flower, TicketPercent,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

// -----------------------------------------------------------------------------
// /pricing — IndianFamilyTree.com-only pricing page.
// - Fetches plans from /api/pricing/plans (site-gated by X-Site-Source
//   or Host header; returns empty on non-IFT sites).
// - Free plan → in-app entitlement, no Stripe hop.
// - Paid plans → POST /api/pricing/razorpay/order → Razorpay Checkout modal
//   → POST /api/pricing/razorpay/verify (signature check) → /pricing/success.
//   (Stripe checkout was deactivated for IFT on 2026-08-29.)
// - Success returns to /pricing/success?session_id=… which polls
//   /api/pricing/status/{session_id} until Stripe confirms.
// -----------------------------------------------------------------------------

const PLAN_ICONS = {
  free:      Flower,
  advance:   Users,
  pro:       TreeDeciduous,
  unlimited: Crown,
};

const PLAN_ACCENTS = {
  free:      "#059669",
  advance:   "#0EA5E9",
  pro:       "#7C3AED",
  unlimited: "#EA580C",
};

// Load Razorpay Checkout JS once, on demand.
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function PricingIft() {
  const [plans, setPlans] = useState([]);
  const [site, setSite] = useState("");
  const [loading, setLoading] = useState(true);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const billing = "yearly";
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code,type,value,plan_keys}
  const [promoBusy, setPromoBusy] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const [sp] = useSearchParams();
  const renewKey = sp.get("renew");
  const [autoRenewDone, setAutoRenewDone] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true);
    try {
      const { data } = await http.post("/pricing/validate-promo", { promo_code: code, billing });
      setPromo(data);
      toast.success(`Code ${data.code} applied — ${data.type === "percent" ? `${data.value}% off` : `₹${(data.value / 100).toLocaleString("en-IN")} off`}${data.value === 100 && data.type === "percent" ? " (FREE!)" : ""}`);
    } catch (err) {
      setPromo(null);
      toast.error(err?.response?.data?.detail || "Invalid promo code.");
    } finally { setPromoBusy(false); }
  };

  useEffect(() => {
    setLoading(true);
    http.get("/pricing/plans")
      .then((r) => {
        setPlans(r.data?.plans || []);
        setSite(r.data?.site || "");
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const isIft = site === "indianfamilytree.com";

  useEffect(() => {
    if (!user?.id) { setUpgradeInfo(null); return; }
    http.get("/pricing/upgrade-options").then((r) => setUpgradeInfo(r.data)).catch(() => setUpgradeInfo(null));
  }, [user?.id]);

  // One-tap renew — /pricing?renew=<plan_key> from the reminder email.
  useEffect(() => {
    if (!renewKey || autoRenewDone || loading || authLoading || !plans.length) return;
    if (!plans.some((p) => p.key === renewKey)) return;
    setAutoRenewDone(true);
    if (!user?.id) {
      toast.info("Please sign in to renew your plan — then tap the plan button below.");
      return;
    }
    startCheckout(renewKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renewKey, autoRenewDone, loading, authLoading, plans, user?.id]);

  const startCheckout = async (planKey) => {
    if (!user?.id) {
      toast.info("Please sign in to choose a plan.");
      return;
    }
    setCheckoutBusy(planKey);
    try {
      if (planKey === "free") {
        const { data } = await http.post("/pricing/checkout", {
          plan_key: planKey,
          origin_url: window.location.origin,
          user_id: user.id,
        });
        if (data?.checkout_url) window.location.href = data.checkout_url;
        else toast.error("Could not start checkout. Please try again.");
        return;
      }
      // Paid plans → Razorpay Checkout (Stripe deactivated for IFT).
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Couldn't load the payment window — check your connection.");
        return;
      }
      const { data } = await http.post("/pricing/razorpay/order", {
        plan_key: planKey,
        origin_url: window.location.origin,
        user_id: user.id,
        billing,
        promo_code: promo?.code || null,
      });
      if (data?.free) {
        toast.success(`${data.plan_name} plan activated free with code ${data.promo_applied}! 🎉`);
        window.location.href = `${window.location.origin}/pricing/success?session_id=${data.session_id}&plan=${planKey}`;
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "IndianFamilyTree.com",
        description: data.is_upgrade ? `Upgrade to ${data.plan_name} (difference + GST)` : `${data.plan_name} plan (yearly, incl. GST)`,
        order_id: data.order_id,
        prefill: data.prefill || {},
        theme: { color: "#EA580C" },
        handler: async (res) => {
          try {
            await http.post("/pricing/razorpay/verify", res);
            window.location.href = `${window.location.origin}/pricing/success?session_id=${data.order_id}&plan=${planKey}`;
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed — please contact support.");
          }
        },
        modal: { ondismiss: () => setCheckoutBusy(null) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed — please try again.");
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
    } finally {
      setCheckoutBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-24 px-6 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }  if (!isIft) {
    return (
      <div className="max-w-3xl mx-auto py-24 px-6 text-center" data-testid="pricing-not-available">
        <SeoHead title="Pricing — Only on IndianFamilyTree.com" description="Pricing is currently only available on IndianFamilyTree.com — the culturally-grounded Indian family history platform." />
        <TreeDeciduous className="w-10 h-10 opacity-40 mx-auto" style={{ color: "#EA580C" }} />
        <h1 className="font-display text-3xl mt-4">Pricing is only on IndianFamilyTree.com</h1>
        <p className="mt-3" style={{ color: "var(--wofy-text-soft)" }}>
          The pricing catalogue on this page belongs to the Indian Family Tree service.
          Visit <a href="https://indianfamilytree.com" className="underline">indianfamilytree.com</a> to
          view plans, or continue exploring the free Celebrate a Life memorials on this site.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/obituaries/hub" className="wofy-btn-primary inline-flex items-center gap-2">
            Free Memorials & Obituaries <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/family-tree-guide" className="wofy-btn-ghost inline-flex items-center gap-2">
            Family Tree Guide
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="pricing-ift-page">
      <SeoHead
        title="Pricing | IndianFamilyTree.com — Family Tree, Memorials & Reunions"
        description="Choose the perfect plan for your family — from a free 20-member tree to unlimited members with a complete Kudumba Sangamam reunion management system. Celebrate a Life memorials are FREE forever."
        url="https://indianfamilytree.com/pricing"
      />

      {/* Hero */}
      <section className="text-center max-w-3xl mx-auto">
        <div className="overline mb-3" style={{ color: "#EA580C" }}>
          FLEXIBLE PLANS FOR EVERY FAMILY SIZE
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.03]">
          Choose the plan that fits <br className="hidden md:block" />
          <span style={{ color: "#EA580C" }}>your family's story.</span>
        </h1>
        <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          Whether you are mapping your immediate household or coordinating a
          multi-branch clan gathering (Kudumba Sangamam),
          IndianFamilyTree.com scales with your needs. And "Celebrate a Life"
          memorials are always <strong>FREE forever</strong>, on every plan.
        </p>
      </section>

      {/* Yearly billing — GST added at checkout */}
      <div className="mt-8 text-center text-xs opacity-70" data-testid="pricing-gst-note">
        All plans are billed yearly. Prices exclude 18% GST, added at checkout.
      </div>
      {upgradeInfo?.current_plan_key && upgradeInfo.current_plan_key !== "free" && (
        <div className="mt-3 text-center text-sm rounded-full inline-flex mx-auto w-full justify-center" data-testid="pricing-current-plan-note">
          <span className="rounded-full border px-4 py-1.5" style={{ borderColor: "#16A34A", background: "#F0FDF4", color: "#166534" }}>
            You're on the <b>{upgradeInfo.current_plan_name}</b> plan{upgradeInfo.renews_on ? ` · renews ${upgradeInfo.renews_on}` : ""}. Upgrade anytime — pay only the difference, keep the same renewal date.
          </span>
        </div>
      )}

      {/* Promo code */}
      <div className="mt-5 flex justify-center">
        <div className="flex items-center gap-2" data-testid="promo-apply-box">
          {promo ? (
            <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold"
              style={{ borderColor: "#16A34A", background: "#F0FDF4", color: "#166534" }} data-testid="promo-applied-badge">
              <TicketPercent className="w-4 h-4" />
              {promo.code} — {promo.type === "percent" ? `${promo.value}% off` : `₹${(promo.value / 100).toLocaleString("en-IN")} off`} at checkout
              {promo.plan_keys?.length ? <span className="opacity-70 font-normal">({promo.plan_keys.join(", ")} only)</span> : null}
              <button type="button" onClick={() => { setPromo(null); setPromoInput(""); }} className="ml-1 opacity-60 hover:opacity-100" data-testid="promo-remove-btn">✕</button>
            </div>
          ) : (
            <>
              <input
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
                placeholder="Promo code"
                className="rounded-full border px-4 py-1.5 text-sm w-36 outline-none uppercase"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="promo-code-field"
              />
              <button
                type="button"
                onClick={applyPromo}
                disabled={promoBusy || !promoInput.trim()}
                className="rounded-full px-4 py-1.5 text-sm font-semibold border transition disabled:opacity-40"
                style={{ borderColor: "#EA580C", color: "#EA580C" }}
                data-testid="promo-apply-btn"
              >
                {promoBusy ? "Checking…" : "Apply"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Plan cards */}
      <section className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="pricing-plans-grid">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.key] || Users;
          const accent = PLAN_ACCENTS[p.key] || "#EA580C";
          const isFeatured = p.key === "unlimited";
          const isFree = p.key === "free";
          const nodeLabel = p.node_limit == null ? "Unlimited" : `Up to ${p.node_limit}`;
          const opt = upgradeInfo?.options?.find((o) => o.key === p.key);
          const hasPaidPlan = upgradeInfo && upgradeInfo.current_plan_key !== "free";
          const isCurrent = hasPaidPlan && upgradeInfo.current_plan_key === p.key;
          const isIncluded = opt?.relation === "included" || (isFree && hasPaidPlan);
          const isDiffUpgrade = !!opt?.is_upgrade;
          const basePaise = isDiffUpgrade ? opt.base_paise : p.price_paise;
          const promoApplies = promo && !isFree && !isCurrent && !isIncluded && basePaise > 0 && (!promo.plan_keys?.length || promo.plan_keys.includes(p.key));
          const promoBase = promoApplies
            ? (promo.type === "percent"
                ? Math.max(0, basePaise - Math.floor((basePaise * promo.value) / 100))
                : Math.max(0, basePaise - promo.value)) : null;
          const promoFinalBase = promoBase != null && promoBase < 100 ? 0 : promoBase;
          const effBase = promoApplies ? promoFinalBase : basePaise;
          const gstPaise = promoApplies
            ? Math.round(effBase * 0.18)
            : (isDiffUpgrade ? opt.gst_paise : (p.gst_paise ?? Math.round(effBase * 0.18)));
          const totalPaise = effBase + gstPaise;
          const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: paise % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
          return (
            <div
              key={p.key}
              data-testid={`pricing-plan-${p.key}`}
              className="wofy-card relative flex flex-col"
              style={
                isFeatured
                  ? {
                      background: "linear-gradient(180deg, rgba(234, 88, 12, 0.06), transparent)",
                      border: `2px solid ${accent}`,
                    }
                  : {}
              }
            >
              {isFeatured && !isCurrent && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white"
                  style={{ background: accent }}
                >
                  MOST POPULAR
                </span>
              )}
              {isCurrent && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white"
                  style={{ background: "#16A34A" }}
                  data-testid={`pricing-current-badge-${p.key}`}
                >
                  YOUR CURRENT PLAN
                </span>
              )}

              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                   style={{ background: `${accent}18`, color: accent }}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="font-display text-2xl">{p.name}</div>
              <div className="text-xs opacity-70 mt-0.5">{nodeLabel} members</div>

              <div className="mt-4 flex items-baseline gap-1.5 flex-wrap">
                {isFree ? (
                  <span className="font-display text-4xl" style={{ color: accent }} data-testid={`pricing-price-${p.key}`}>₹0</span>
                ) : promoApplies ? (
                  <>
                    <span className="font-display text-4xl" style={{ color: "#16A34A" }} data-testid={`pricing-price-${p.key}`}>
                      {promoFinalBase === 0 ? "FREE" : inr(promoFinalBase)}
                    </span>
                    {promoFinalBase !== 0 && <span className="text-sm font-semibold" style={{ color: "#16A34A" }}>+ GST</span>}
                    <span className="text-sm line-through opacity-50" data-testid={`pricing-price-original-${p.key}`}>
                      {inr(basePaise)}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl" style={{ color: accent }} data-testid={`pricing-price-${p.key}`}>{inr(basePaise)}</span>
                    <span className="text-sm font-semibold opacity-80">+ GST</span>
                  </>
                )}
                {!isFree && (
                  <span className="text-xs opacity-70">per year</span>
                )}
              </div>
              {!isFree && (
                <div className="mt-1 text-[11px] opacity-70" data-testid={`pricing-total-${p.key}`}>
                  {promoApplies && promoFinalBase === 0
                    ? null
                    : `${inr(totalPaise)} total incl. 18% GST`}
                </div>
              )}
              {isDiffUpgrade && (
                <div className="mt-1 text-[11px] font-semibold" style={{ color: "#16A34A" }} data-testid={`pricing-upgrade-note-${p.key}`}>
                  Upgrade price — pay only the difference from your {upgradeInfo.current_plan_name} plan
                </div>
              )}
              {promoApplies && (
                <div className="mt-1 text-[11px] font-semibold" style={{ color: "#16A34A" }} data-testid={`pricing-promo-note-${p.key}`}>
                  Code {promo.code} applied{promoFinalBase === 0 ? " — completely free!" : ""}
                </div>
              )}
              <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
                {p.highlight}
              </p>

              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => startCheckout(p.key)}
                disabled={checkoutBusy === p.key || isCurrent || isIncluded}
                data-testid={`pricing-choose-${p.key}`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02] disabled:hover:scale-100"
                style={{ background: isCurrent || isIncluded ? "#94A3B8" : accent, opacity: checkoutBusy === p.key ? 0.55 : 1 }}
              >
                {checkoutBusy === p.key ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrent ? (
                  <>Current plan <Check className="w-4 h-4" /></>
                ) : isIncluded ? (
                  <>Included in your plan</>
                ) : isDiffUpgrade ? (
                  <>Upgrade — pay {inr(totalPaise)} <ArrowRight className="w-4 h-4" /></>
                ) : isFree ? (
                  <>Start free <ArrowRight className="w-4 h-4" /></>
                ) : (
                  <>Choose {p.name} <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          );
        })}
      </section>

      {/* Celebrate a Life callout */}
      <section
        className="mt-16 rounded-3xl p-8 md:p-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center"
        style={{
          background: "linear-gradient(135deg, rgba(234, 88, 12, 0.10), rgba(139, 92, 246, 0.06))",
          border: "1px solid rgba(234, 88, 12, 0.28)",
        }}
        data-testid="pricing-celebrate-a-life"
      >
        <div>
          <div className="overline mb-2" style={{ color: "#EA580C" }}>
            CELEBRATE A LIFE — FREE FOREVER
          </div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Every memorial is <span style={{ color: "#EA580C" }}>free</span>, on every plan.
          </h3>
          <p className="mt-3 text-sm md:text-base" style={{ color: "var(--wofy-text-soft)" }}>
            Digital life story · Anniversary & Tithi reminders · Shared photo wall ·
            Virtual candles · Direct link into your family tree. Because
            preserving an ancestor's memory should never cost your family a
            single rupee.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Link
            to="/obituaries/submit"
            data-testid="pricing-cal-cta"
            className="wofy-btn-primary inline-flex items-center justify-center gap-2 !text-base !px-5 !py-3"
            style={{ background: "#EA580C" }}
          >
            <Sparkles className="w-4 h-4" /> Create a Free Memorial <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/obituaries/hub"
            className="wofy-btn-ghost inline-flex items-center justify-center gap-2 !text-base"
          >
            Browse Memorials
          </Link>
        </div>
      </section>

      {/* Trust strip */}
      <section className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs opacity-70">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure Razorpay checkout — UPI · Cards · Netbanking</span>
        <span className="inline-flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5" /> Kudumba Sangamam ready</span>
        <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Family collaboration</span>
        <span className="inline-flex items-center gap-1.5"><TreeDeciduous className="w-3.5 h-3.5" /> Cultural metadata</span>
      </section>
    </div>
  );
}


// ---------------- Success + Cancel pages ------------------------------------

export function PricingSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const planKey = sp.get("plan");
  const [status, setStatus] = useState({ loading: true, paid: planKey === "free" });

  useEffect(() => {
    if (planKey === "free" || !sessionId) {
      setStatus({ loading: false, paid: true });
      return;
    }
    let cancelled = false;
    const poll = async (attempt = 0) => {
      try {
        const { data } = await http.get(`/pricing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ loading: false, paid: true });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus({ loading: false, paid: false, error: data.payment_status });
          return;
        }
      } catch {
        // ignore transient errors
      }
      if (attempt < 10) {
        setTimeout(() => poll(attempt + 1), 2000);
      } else {
        setStatus({ loading: false, paid: false, error: "timeout" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, planKey]);

  return (
    <div className="max-w-2xl mx-auto py-24 px-6 text-center" data-testid="pricing-success-page">
      {status.loading ? (
        <>
          <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-70" />
          <h1 className="font-display text-3xl mt-4">Confirming your payment…</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Razorpay usually takes a couple of seconds. Please don't refresh.
          </p>
        </>
      ) : status.paid ? (
        <>
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
               style={{ background: "rgba(16, 185, 129, 0.15)", color: "#059669" }}>
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-display text-4xl mt-4">Welcome to the family, literally.</h1>
          <p className="mt-3 text-base" style={{ color: "var(--wofy-text-soft)" }}>
            Your plan is now active. Start mapping your tree, add your first
            ancestors, and invite relatives to help fill in every branch.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link to="/heritage" className="wofy-btn-primary inline-flex items-center gap-2" style={{ background: "#EA580C" }}>
              Open my Family Tree <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/family-tree-guide" className="wofy-btn-ghost">
              How-to guide
            </Link>
          </div>
        </>
      ) : (
        <>
          <h1 className="font-display text-3xl">Something didn't go through.</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            {status.error === "timeout"
              ? "We're still waiting for the payment to confirm — please refresh in a minute."
              : "The payment couldn't be completed. You have not been charged."}
          </p>
          <div className="mt-6">
            <Link to="/pricing" className="wofy-btn-primary inline-flex items-center gap-2" style={{ background: "#EA580C" }}>
              Back to Pricing
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export function PricingCancel() {
  return (
    <div className="max-w-2xl mx-auto py-24 px-6 text-center" data-testid="pricing-cancel-page">
      <h1 className="font-display text-3xl">No worries — you can pick a plan anytime.</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Your card was not charged. Every free feature (including memorials) is
        still fully available.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <Link to="/pricing" className="wofy-btn-primary inline-flex items-center gap-2" style={{ background: "#EA580C" }}>
          Back to Pricing
        </Link>
        <Link to="/family-tree-guide" className="wofy-btn-ghost">
          Family Tree Guide
        </Link>
      </div>
    </div>
  );
}
